'use es6';

import { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import text from 'I18n/utils/unescapedText';
import { findWhere, findKey, debounce, isEqual } from 'underscore';
import Small from 'UIComponents/elements/Small';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIAlert from 'UIComponents/alert/UIAlert';
import GoToWebinarSelector from 'ContentComponents/cmv2/components/GoToWebinarSelector';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import FormResponse from 'ContentComponents/cmv2/form/FormResponse';
import FormWorkflowDependencies from 'ContentComponents/cmv2/form/formAutomation/connectedWorkflows/FormWorkflowDependencies';
import FollowupEmail from 'ContentComponents/cmv2/form/formAutomation/followupEmail/FollowupEmail';
import ContentRoutes from 'ContentUtils/Routes';
import { CONFLICT } from 'ContentUtils/enums/HttpStatusCodes';
import * as pagesSelectors from 'ContentData/selectors/pages';
import * as GoToWebinarActions from 'ContentData/actions/GoToWebinar';
import * as goToWebinarSelectors from 'ContentData/selectors/goToWebinar';
import * as FormsActions from 'ContentData/actions/Forms';
import * as formsSelectors from 'ContentData/selectors/forms';
import { FORM, RICH_TEXT } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { HUBSPOT, TICKET_FORM, VIDEO_FORM } from 'ContentUtils/constants/FormTypes';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import withCreateResourceModal from '../decorators/withCreateResourceModal';
import { compose } from 'redux';
import { userInfoSync } from 'hub-http/userInfo';
import FormWithInlineEditing from 'ContentComponents/cmv2/form/FormWithInlineEditingContainer';
import NeedMoreAdvancedFeatures from 'ContentComponents/cmv2/form/NeedMoreAdvancedFeatures';
import { FORM_RESPONSE_TYPE_OPTIONS } from 'ContentComponents/cmv2/constants';
import MarketingContactsWarning from 'ContentComponents/cmv2/form/MarketingContactsWarning';
import FormsContext from 'ContentComponents/context/FormsContext';
import FormErrorBoundary from 'ContentComponents/cmv2/form/FormErrorBoundary';

// TODO: restore after figuring out why this field unmounts/remounts in
// the IDE after onFieldChange is called
// import withPartials from 'ContentComponents/cmv2/decorators/withPartials';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EVENT_TRACKING_KEYS = {
  FORM_CREATE_NEW: 'form-create-new',
  FORM_EDIT: 'form-edit',
  FORM_SELECT: 'select-form'
};
const INLINE_EVENT_TRACKING_KEYS = {
  FORM_CREATE_NEW: 'form-inline-create-new',
  FORM_CLONE: 'form-inline-clone',
  FORM_SELECT: 'forms-inline-select-form',
  FORM_FIELD_ADD: 'form-inline-field-add',
  FORM_FIELD_EDIT: 'form-inline-field-edit',
  FORM_FIELD_REMOVE: 'form-inline-field-remove',
  FORM_FIELD_REORDER: 'form-inline-field-reorder',
  GO_TO_FULL_EDITOR: 'form-inline-gotofulleditor',
  FORM_GDPR_CHANGE: 'form-inline-gdprchange',
  FORM_AUTOMATION_CHANGE: 'form-inline-automation-change',
  FORM_CREATE_MARKETING_CONTACT_CHANGE: 'form-inline-create-marketing-contact-change',
  VIEW_FORM_SUBMISSIONS: 'form-inline-viewformsubmissions',
  FORM_WORKFLOW_ADD: 'form-inline-workflow-add',
  FORM_WORKFLOW_EDIT: 'form-inline-workflow-edit',
  FORM_WORKFLOW_REMOVE: 'form-inline-workflow-remove'
};
const FORM_PROPERTY_TYPE_OBJECT_ID = {
  CONTACT: '0-1',
  COMPANY: '0-2',
  TICKET: '0-5'
};
const DecoratedAsyncSelector = withCreateResourceModal(FORM)(AsyncSelector);
const i18nKey = key => `ContentComponents.cmv2.fields.form.${key}`;
const isRawHtmlEnabled = form => {
  let enabled = false;
  if (form) {
    const metaData = form.metaData;
    const renderRawHtml = findWhere(metaData, {
      name: 'renderRawHtml'
    });
    enabled = renderRawHtml ? !!renderRawHtml.value : false;
  } else {
    enabled = false;
  }
  return enabled;
};
export const NoFormsWriteScopeMessage = () => /*#__PURE__*/_jsx(UIAlert, {
  titleText: /*#__PURE__*/_jsx(FormattedMessage, {
    message: i18nKey('noFormsWrite.title')
  }),
  type: "warning",
  children: /*#__PURE__*/_jsx("p", {
    className: "p-bottom-4",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('noFormsWrite.body')
    })
  })
});
function mapStateToProps(state, props) {
  const formGuid = typeof props.value === 'object' ? props.value.form_id : props.value;
  const selectedForm = formsSelectors.getForm(state, {
    formGuid
  });
  return {
    pages: pagesSelectors.getPages(state),
    goToWebinarEnabled: goToWebinarSelectors.getIsGoToWebinarEnabled(state),
    forms: formsSelectors.getForms(state),
    options: formsSelectors.getFormsOptions(state),
    defaultOptions: formsSelectors.getDefaultFormsOptions(state),
    selectedForm,
    isOptedInToInlineEditing: !!(props.isOptedInToInlineEditing || formsSelectors.getIsOptedIntoInlineEdit(state)),
    canActionInlineEditOptIn: formsSelectors.getCanActionInlineEditOptIn(state),
    optedInInlineEditingRequestSucceeded: formsSelectors.getInlineOptedInRequestSucceeded(state)
  };
}
const mapDispatchToProps = {
  fetchWebinarSettings: GoToWebinarActions.fetchSettings,
  fetchForm: FormsActions.fetchForm,
  fetchFilteredForms: FormsActions.fetchFilteredForms,
  createResource: FormsActions.createForm,
  clearCache: FormsActions.clearCache,
  saveCache: FormsActions.saveCache,
  fetchInlineEdtingOptIn: FormsActions.fetchIsOptedIntoInlineEditing,
  cloneForm: FormsActions.cloneForm
};
class FormField extends PureComponent {
  constructor(props) {
    super(props);
    this.onClickAddButton = this.onClickAddButton.bind(this);
    this.onFormChange = this.onFormChange.bind(this);
    this.updatePostSubmissionOptions = this.updatePostSubmissionOptions.bind(this);
    this.onFollowupEmailIdChange = this.onFollowupEmailIdChange.bind(this);
    this.onWebinarChange = this.onWebinarChange.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.onEditForm = this.onEditForm.bind(this);
    this.onCreateForm = this.onCreateForm.bind(this);
    this.openForm = this.openForm.bind(this);
    this.openFormFn = this.openFormFn.bind(this);
    this.openSubmissions = this.openSubmissions.bind(this);
    this.openSubmissionsFn = this.openSubmissionsFn.bind(this);
    this.maybeRenderMarketingContactsWarning = this.maybeRenderMarketingContactsWarning.bind(this);
    this.maybeRenderInlineEditingOptIn = this.maybeRenderInlineEditingOptIn.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this._isMounted = false;
    this.state = {
      input: '',
      rawHtmlEnabled: false
    };
  }
  componentDidMount() {
    this._isMounted = true;
    if (this.formGuid) {
      this.loadForm(this.formGuid);
    }
    if (this.props.goToWebinarEnabled == null) {
      this.props.fetchWebinarSettings();
    }
    this.props.fetchInlineEdtingOptIn();
  }
  componentDidUpdate(prevProps) {
    const {
      forms,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    const prevFormGuid = this.getFormGuid(prevValue);
    const formGuid = this.getFormGuid(value);
    if (!isEqual(formGuid, prevFormGuid) && !forms[formGuid]) {
      this.loadForm(formGuid);
    }
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  get value() {
    const {
      value
    } = this.props;
    if (!value) {
      return {};
    }
    const formGuid = this.getFormGuid(value);
    const {
      response_type: responseType = FORM_RESPONSE_TYPE_OPTIONS.INLINE,
      message,
      redirect_id: redirectId,
      redirect_url: redirectUrl,
      gotowebinar_webinar_key: webinarKey,
      form_type: formType,
      followup_email_id: followupEmailId
    } = typeof value === 'object' ? value : {};
    return {
      form_id: formGuid,
      response_type: responseType,
      message,
      redirect_id: redirectId,
      redirect_url: redirectUrl,
      gotowebinar_webinar_key: webinarKey,
      form_type: formType,
      followup_email_id: followupEmailId
    };
  }
  get formGuid() {
    const {
      value
    } = this.props;
    if (!value) return null;
    return this.getFormGuid(value);
  }
  get fieldKey() {
    const {
      fieldKey
    } = this.props;
    return fieldKey && fieldKey.join('.');
  }
  get scopes() {
    if (!this._scopes) {
      const {
        user: {
          scopes = []
        }
      } = userInfoSync();
      this._scopes = scopes;
    }
    return this._scopes;
  }
  get hasWorkflowsAccess() {
    return this.scopes.includes('workflows-access');
  }
  get hasWorkflowsAppAccess() {
    return this.scopes.includes('workflows-app-access');
  }
  get hasWorkflowsWriteAccess() {
    return this.scopes.includes('workflows-edit-team-owned');
  }
  get hasWorkflowsDeleteAccess() {
    return this.scopes.includes('workflows-delete-team-owned');
  }
  get hasAutomatedEmailEditorAccess() {
    return this.scopes.includes('email-type-workflows-access');
  }
  get hasEmailMarketingAccess() {
    return this.scopes.includes('email-marketing-product-access');
  }
  get hasInlineEmailEditorAccess() {
    const {
      isMarketingEmailApprovalsEnabled
    } = this.props;
    return !isMarketingEmailApprovalsEnabled && this.scopes.includes('workflows-contextual-create-inline-email-access');
  }
  get hasFormsWriteAccess() {
    return this.scopes.includes('forms-write');
  }
  get hasMarketingContactsPermissions() {
    return this.scopes.includes('marketable-contacts-write');
  }
  get hasMarketingContactsAccess() {
    return this.scopes.includes('marketable-contacts-access');
  }
  get isSuperAdmin() {
    return this.scopes.includes('super-admin');
  }
  getFormGuid(form) {
    return typeof form === 'object' ? form.form_id : form;
  }
  getTrackEventFunction(eventKey) {
    return options => this.trackEvent(eventKey, options);
  }
  trackEvent(eventKey, options) {
    const {
      trackFormAction
    } = this.props;
    if (trackFormAction) {
      trackFormAction(eventKey, options);
    }
  }
  hasNewFormExperienceDependencies() {
    const {
      FormDefinition
    } = this.props;
    return FormDefinition;
  }
  canShowNewFormExperienceOptInOut() {
    const {
      optedInInlineEditingRequestSucceeded,
      canActionInlineEditOptIn,
      allowFormEditing
    } = this.props;
    return allowFormEditing && this.hasNewFormExperienceDependencies() && optedInInlineEditingRequestSucceeded && canActionInlineEditOptIn;
  }
  canShowNewFormExperience() {
    const {
      allowFormEditing,
      disable_inline_form_editing = false,
      isOptedInToInlineEditing
    } = this.props;
    return isOptedInToInlineEditing && this.hasNewFormExperienceDependencies() && allowFormEditing && !disable_inline_form_editing;
  }
  loadForm(formGuid) {
    this.props.fetchForm(formGuid).then(form => {
      const {
        value
      } = this.props;
      if (this._isMounted) {
        this.setState({
          rawHtmlEnabled: isRawHtmlEnabled(form)
        });
      }
      if (typeof value === 'object' && value && !value.form_type) {
        this.props.onFieldChange(Object.assign({}, value, {
          form_type: form.formType
        }));
      }
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  loadOptions(input = '', callback, pagination = {
    offset: 0
  }) {
    const {
      required_property_types = []
    } = this.props;
    let offset = pagination.offset;
    if (input !== this.state.input) {
      if (this._isMounted) {
        this.setState({
          input
        });
      }
      this.props.clearCache();
      offset = 0;
    }
    const formTypes = this.props.forVideo ? [HUBSPOT, TICKET_FORM, VIDEO_FORM] : [HUBSPOT, TICKET_FORM];
    const query = {
      name: input,
      limit: DEFAULT_PAGE_SIZE,
      offset,
      formTypes
    };
    if (required_property_types.length) {
      query.objectTypeId = required_property_types.map(propertyType => FORM_PROPERTY_TYPE_OBJECT_ID[propertyType]);
    }
    this.props.fetchFilteredForms(query).then(response => {
      const {
        total
      } = response;
      callback(null, {
        options: this.props.options,
        pagination: {
          hasMore: total > this.props.options.length,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  updatePostSubmissionOptions(form, newFormGuid) {
    const {
      onFieldChange,
      pages,
      value
    } = this.props;
    let newValue;
    if (form) {
      if (this._isMounted) {
        this.setState({
          rawHtmlEnabled: isRawHtmlEnabled(form)
        });
      }
      if (form.inlineMessage) {
        newValue = {
          form_id: form.guid,
          response_type: FORM_RESPONSE_TYPE_OPTIONS.INLINE,
          message: form.inlineMessage,
          gotowebinar_webinar_key: null,
          form_type: form.formType
        };
      } else if (form.redirect) {
        const pageId = findKey(pages, pageData => pageData.absoluteUrl === form.redirect);
        const page = pages[pageId];
        newValue = {
          form_id: form.guid,
          response_type: FORM_RESPONSE_TYPE_OPTIONS.REDIRECT,
          redirect_url: page ? null : form.redirect,
          redirect_id: page ? page.id : null,
          gotowebinar_webinar_key: null,
          form_type: form.formType
        };
      } else {
        newValue = {
          form_id: form.guid,
          response_type: FORM_RESPONSE_TYPE_OPTIONS.INLINE,
          message: text(i18nKey('default.message')),
          gotowebinar_webinar_key: null,
          form_type: form.formType
        };
      }
    } else {
      newValue = typeof value === 'object' ? Object.assign({}, this.value, {
        form_id: newFormGuid,
        gotowebinar_webinar_key: null
      }) : newFormGuid;
    }
    onFieldChange(newValue);
  }
  onFormChange(newFormGuid) {
    this.props.fetchForm(newFormGuid).then(form => {
      this.updatePostSubmissionOptions(form, newFormGuid);
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  onWebinarChange(webinarKey) {
    this.props.onFieldChange(Object.assign({}, this.value, {
      gotowebinar_webinar_key: webinarKey
    }));
  }
  onFollowupEmailIdChange(followupEmailId) {
    this.props.onFieldChange(Object.assign({}, this.value, {
      followup_email_id: followupEmailId
    }));
  }
  openForm(formGuid, params = {}, objectTypeId, name) {
    const redirectUrl = this.props.redirectUrl || window.location.href;
    if (this.props.forVideo) {
      params.isExternalEditor = 'video';
    }
    const formUrl = ContentRoutes.forms(formGuid, redirectUrl, params, objectTypeId, name);
    window.location.assign(formUrl);
  }
  openSubmissions(formGuid) {
    const redirectUrl = this.props.redirectUrl || window.location.href;
    const submissionsUrl = ContentRoutes.formSubmissions(formGuid, redirectUrl);
    window.location.assign(submissionsUrl);
  }
  onEditForm() {
    this.trackEvent(EVENT_TRACKING_KEYS.FORM_EDIT);
    this.openForm(this.formGuid);
  }
  onClickAddButton() {
    this.trackEvent(EVENT_TRACKING_KEYS.FORM_CREATE_NEW);
  }
  openFormFn(objectTypeId, name) {
    this.trackEvent(INLINE_EVENT_TRACKING_KEYS.GO_TO_FULL_EDITOR);
    this.openForm(this.formGuid, {
      isDraftUpdate: true
    }, objectTypeId, name);
  }
  openSubmissionsFn() {
    this.trackEvent(INLINE_EVENT_TRACKING_KEYS.VIEW_FORM_SUBMISSIONS);
    this.openSubmissions(this.formGuid);
  }
  onCreateForm(formName, {
    onConflictError = () => {},
    onSuccess = () => {},
    shouldRedirect = true
  } = {}) {
    this.props.createResource(formName).then(result => {
      if (result && result.status === CONFLICT) {
        onConflictError();
        return;
      }
      const newFormGuid = result && result.guid;
      if (newFormGuid) {
        this.props.saveCache(newFormGuid);
        this.onFormChange(newFormGuid);
        onSuccess();
        if (shouldRedirect) {
          const redirectToNewForm = (...partialArgs) => this.openForm(newFormGuid, ...partialArgs);
          if (this.props.onRedirect) {
            this.props.onRedirect(redirectToNewForm);
          } else {
            redirectToNewForm();
          }
        }
      }
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  maybeRenderInlineEditingOptIn(objectTypeId, name) {
    if (!this.canShowNewFormExperienceOptInOut()) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "p-bottom-2",
      children: /*#__PURE__*/_jsx(NeedMoreAdvancedFeatures, {
        hasPermissions: this.hasFormsWriteAccess,
        openFormFn: () => this.openFormFn(objectTypeId, name)
      })
    });
  }
  maybeRenderMarketingContactsWarning() {
    const {
      selectedForm
    } = this.props;
    const hasNoFormSelected = !selectedForm;
    const hasNoMarketingContactAccess = !this.hasMarketingContactsAccess;
    if (hasNoMarketingContactAccess || hasNoFormSelected) return null;
    const {
      createMarketableContact
    } = selectedForm;
    return /*#__PURE__*/_jsx("div", {
      className: "m-bottom-2 m-top-2",
      children: /*#__PURE__*/_jsx(MarketingContactsWarning, {
        enabled: createMarketableContact,
        useEditor: true,
        openFormFn: () => {
          this.trackEvent(INLINE_EVENT_TRACKING_KEYS.GO_TO_FULL_EDITOR);
          this.openForm(this.formGuid);
        }
      })
    });
  }
  renderResponse() {
    const {
      componentsByType,
      fieldKey,
      forVideo,
      modulePath,
      onFieldChange,
      readOnly,
      useOnDark
    } = this.props;
    if (forVideo) {
      return null;
    }
    return /*#__PURE__*/_jsx(FormResponse, {
      componentsByType: componentsByType,
      fieldKey: fieldKey,
      hasFormsWriteAccess: true,
      modulePath: modulePath,
      onFieldChange: onFieldChange,
      readOnly: readOnly,
      useOnDark: useOnDark,
      value: this.value
    });
  }
  renderGoToWebinarSelect() {
    if (!this.props.goToWebinarEnabled || this.props.forVideo) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "p-top-4",
      children: /*#__PURE__*/_jsx(GoToWebinarSelector, {
        formGuid: this.value.form_id,
        onWebinarChange: this.onWebinarChange,
        useOnDark: this.props.useOnDark,
        webinarKey: this.value.gotowebinar_webinar_key
      })
    });
  }
  renderWorkflowSelect() {
    if (!(this.formGuid && this.hasWorkflowsAccess && this.hasFormsWriteAccess) || this.props.forVideo) {
      return null;
    }
    const {
      contentId,
      readOnly,
      useOnDark
    } = this.props;
    return /*#__PURE__*/_jsx(FormWorkflowDependencies, {
      contentId: contentId,
      formGuid: this.formGuid,
      hasFormsWriteAccess: this.hasFormsWriteAccess,
      hasWorkflowsAccess: this.hasWorkflowsAccess,
      hasWorkflowsAppAccess: this.hasWorkflowsAppAccess,
      hasWorkflowsDeleteAccess: this.hasWorkflowsDeleteAccess,
      hasWorkflowsWriteAccess: this.hasWorkflowsWriteAccess,
      onAddWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_ADD),
      onEditWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_EDIT),
      onRemoveWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_REMOVE),
      readOnly: readOnly,
      useOnDark: useOnDark
    });
  }
  renderFollowupEmail() {
    const {
      clearLegacyFollowupEmail,
      contentId,
      followupEmailSelectedForForm,
      forVideo,
      legacyFollowupEmailId,
      readOnly
    } = this.props;
    if (!(this.hasEmailMarketingAccess && this.formGuid && contentId) || forVideo) {
      return null;
    }
    const {
      followup_email_id: followupEmailId
    } = this.value;
    return /*#__PURE__*/_jsx(FollowupEmail, {
      clearLegacyFollowupEmail: clearLegacyFollowupEmail,
      contentId: contentId,
      followupEmailId: followupEmailId,
      followupEmailSelectedForForm: followupEmailSelectedForForm,
      formGuid: this.formGuid,
      hasAutomatedEmailEditorAccess: this.hasAutomatedEmailEditorAccess,
      hasInlineEmailEditorAccess: this.hasInlineEmailEditorAccess,
      hasWorkflowsAppAccess: this.hasWorkflowsAppAccess,
      legacyFollowupEmailId: legacyFollowupEmailId,
      onChangeFollowupEmailEvent: key => this.trackEvent(key),
      onFollowupEmailIdChange: this.onFollowupEmailIdChange,
      readOnly: readOnly
    });
  }
  renderWithInlineFormEditing() {
    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_createElement(FormWithInlineEditing, Object.assign({}, this.props, {
        key: this.formGuid,
        DraggableComponents: this.props.DraggableComponents,
        FormDefinition: this.props.FormDefinition,
        loadOptions: this.loadOptions,
        rawHtmlEnabled: this.rawHtmlEnabled,
        value: this.value,
        hasAutomatedEmailEditorAccess: this.hasAutomatedEmailEditorAccess,
        hasEmailMarketingAccess: this.hasEmailMarketingAccess,
        hasFormsWriteAccess: this.hasFormsWriteAccess,
        hasInlineEmailEditorAccess: this.hasInlineEmailEditorAccess,
        hasMarketingContactsAccess: this.hasMarketingContactsAccess,
        hasMarketingContactsPermissions: this.hasMarketingContactsPermissions,
        hasWorkflowsAccess: this.hasWorkflowsAccess,
        hasWorkflowsAppAccess: this.hasWorkflowsAppAccess,
        hasWorkflowsDeleteAccess: this.hasWorkflowsDeleteAccess,
        hasWorkflowsWriteAccess: this.hasWorkflowsWriteAccess,
        isGoToWebinarEnabled: this.props.goToWebinarEnabled,
        allowFormEditingDnd: this.props.allowFormEditingDnd,
        maybeRenderInlineEditingOptIn: this.maybeRenderInlineEditingOptIn,
        fetchConsentCopyFn: this.props.fetchConsentCopyFn,
        cloneFormFn: (...args) => {
          this.trackEvent(INLINE_EVENT_TRACKING_KEYS.FORM_CLONE);
          return this.props.cloneForm(...args);
        },
        createFormFn: (...args) => {
          this.trackEvent(INLINE_EVENT_TRACKING_KEYS.FORM_CREATE_NEW);
          return this.onCreateForm(...args);
        },
        openFormFn: this.openFormFn,
        openSubmissionsFn: this.openSubmissionsFn,
        onFormChange: ({
          target: {
            value: newFormGuid
          }
        }) => {
          this.trackEvent(INLINE_EVENT_TRACKING_KEYS.FORM_SELECT, {
            formId: newFormGuid
          });
          this.onFormChange(newFormGuid);
        },
        onOpenEditFieldPanelEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_FIELD_EDIT),
        onAddFormFieldEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_FIELD_ADD),
        onRemoveFormFieldEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_FIELD_REMOVE),
        onReorderFieldsEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_FIELD_REORDER),
        onGdprChangeEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_GDPR_CHANGE),
        onAutomationChange: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_AUTOMATION_CHANGE),
        onIsCreatingMarketingContactsChange: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_CREATE_MARKETING_CONTACT_CHANGE),
        onAddWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_ADD),
        onEditWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_EDIT),
        onRemoveWorkflowEvent: this.getTrackEventFunction(INLINE_EVENT_TRACKING_KEYS.FORM_WORKFLOW_REMOVE),
        onChangeFollowupEmailEvent: key => this.trackEvent(key),
        onFollowupEmailIdChange: this.onFollowupEmailIdChange,
        followupEmailSelectedForForm: this.props.followupEmailSelectedForForm,
        extraDropdownFooterContent: this.renderFormFilteringAlert()
      }))
    });
  }
  renderFormFilteringAlert() {
    const {
      required_property_types = []
    } = this.props;
    if (required_property_types.length) {
      const requiredProperties = /*#__PURE__*/_jsx(FormattedList, {
        children: required_property_types.map(propertType => /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`requiredPropertyTypes.${propertType}`)
        }, propertType))
      });
      return /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('requiredPropertyTypes.infoText'),
          options: {
            requiredProperties
          }
        })
      });
    }
    return null;
  }
  render() {
    const {
      componentsByType,
      value,
      readOnly,
      required
    } = this.props;
    if (this.canShowNewFormExperience()) {
      return /*#__PURE__*/_jsx(FormsContext.Provider, {
        value: {
          RichTextComponent: componentsByType[RICH_TEXT]
        },
        children: /*#__PURE__*/_jsx(FormErrorBoundary, {
          children: this.renderWithInlineFormEditing()
        })
      });
    }
    const formSelect = /*#__PURE__*/_jsx(UIFormControl, {
      description: !this.state.rawHtmlEnabled && !this.props.forVideo && /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('rawHTMLWarning.description')
      }),
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(DecoratedAsyncSelector, Object.assign({
          "data-selenium": `${this.fieldKey}:formSelect`,
          disabled: readOnly
        }, this.props, {
          onChange: ({
            target: {
              value: newFormGuid
            }
          }) => {
            this.trackEvent(EVENT_TRACKING_KEYS.FORM_SELECT);
            this.onFormChange(newFormGuid);
          },
          clearable: !required,
          extraDropdownFooterContent: this.renderFormFilteringAlert(),
          onClickAddButton: this.onClickAddButton,
          value: this.value.form_id,
          loadOptions: this.debouncedLoadOptions,
          onEditResource: this.onEditForm,
          onCreateResource: this.onCreateForm
        }))
      })
    });
    if (!value || typeof value === 'string') {
      return formSelect;
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [this.maybeRenderInlineEditingOptIn(), formSelect, this.maybeRenderMarketingContactsWarning(), this.renderResponse(), this.renderGoToWebinarSelect(), this.renderFollowupEmail(), this.renderWorkflowSelect()]
    });
  }
}
FormField.propTypes = {
  allowFormEditing: PropTypes.bool,
  // app specific flag
  allowFormEditingDnd: PropTypes.bool,
  // app specific flag
  clearCache: PropTypes.func.isRequired,
  clearLegacyFollowupEmail: PropTypes.func,
  componentsByType: PropTypes.object.isRequired,
  contentId: PropTypes.number,
  createResource: PropTypes.func.isRequired,
  currentFieldKey: PropTypes.array,
  defaultOptions: PropTypes.array,
  disable_inline_form_editing: PropTypes.bool,
  fetchForm: PropTypes.func.isRequired,
  fetchWebinarSettings: PropTypes.func.isRequired,
  fieldKey: PropTypes.array,
  followupEmailSelectedForForm: PropTypes.object,
  forms: PropTypes.object,
  formsOptions: PropTypes.arrayOf(PropTypes.object),
  goToWebinarEnabled: PropTypes.bool,
  isMarketingEmailApprovalsEnabled: PropTypes.bool,
  legacyFollowupEmailId: PropTypes.number,
  modulePath: PropTypes.string,
  onFieldChange: PropTypes.func.isRequired,
  onRedirect: PropTypes.func,
  options: PropTypes.array.isRequired,
  pages: PropTypes.object,
  readOnly: PropTypes.bool,
  redirectUrl: PropTypes.string,
  required: PropTypes.bool.isRequired,
  saveCache: PropTypes.func.isRequired,
  setFieldKey: PropTypes.func.isRequired,
  trackFormAction: PropTypes.func,
  useOnDark: PropTypes.bool,
  selectedForm: PropTypes.object,
  FormDefinition: PropTypes.func,
  DraggableComponents: PropTypes.object,
  fetchConsentCopyFn: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  isOptedInToInlineEditing: PropTypes.bool.isRequired,
  fetchInlineEdtingOptIn: PropTypes.func.isRequired,
  openFormFn: PropTypes.func,
  optedInInlineEditingRequestSucceeded: PropTypes.bool.isRequired,
  canActionInlineEditOptIn: PropTypes.bool.isRequired,
  cloneForm: PropTypes.func,
  defaultFormNamePrefix: PropTypes.string,
  forVideo: PropTypes.bool,
  fetchFilteredForms: PropTypes.func.isRequired,
  required_property_types: PropTypes.arrayOf(PropTypes.string)
};
FormField.defaultProps = {
  defaultOptions: [],
  fieldKey: [],
  forms: {},
  options: [],
  pages: {},
  readOnly: false,
  allowFormEditing: false,
  value: {
    response_type: 'inline',
    message: ''
  }
};
export default compose(connect(mapStateToProps, mapDispatchToProps))(FormField);